import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogFooter } from 'shared/components/ui/dialog';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import { Search, CirclePlus, CircleX, X } from 'lucide-react';
import finecoLogo from 'shared/assets/images/fineco.png';
import constraintsProductMicrocatsStore from 'shared/store/constraintsProductsMicrocatsStore';
import colorsStore from 'shared/store/colorsStore';
import activityPanelStore from 'shared/store/activityPanelStore';
import constraintsStore from 'shared/store/constraintsStore';
import {
  ProductConstraints,
  ProductItemConstraint,
  PortfolioProductConstraint,
  ProductDisplayRequestItem,
  apiToUiScale,
  uiToApiScale,
  getListsContainingProduct,
  HoldMode,
} from 'types/ProductTypes';
import Unlock from 'shared/assets/svg/LockOpen';
import Lock from 'shared/assets/svg/LockClosed';
import LockSell from 'shared/assets/svg/LockSell';
import { AmountInput } from 'shared/components/ui/AmountInput';
import { IconNote } from 'shared/components/ui/IconNote';

import styles from './ProductContent.module.scss';
import { formatItalianNumber } from 'utils/formatUtils';
import { Wallet } from 'shared/assets/svg';

type ConstraintsPanelMode = 'builder' | 'rebalancing' | 'diagnosis';

/** Set to true to restore Peso(%) Min/Max columns in rebalancing mode. */
const SHOW_PESO_COLUMNS_IN_REBALANCING = false;

interface ProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData: ProductConstraints;
  onSave: (data: ProductConstraints) => void;
  mode: ConstraintsPanelMode;
}

// Local state types (0-100 scale for UI, stored as strings for editing)
// No display fields - those come from the cache
interface ProductLocal {
  weight_min: string;
  weight_max: string;
  value_min: string;
  value_max: string;
}

interface PortfolioProductLocal extends ProductLocal {
  hold_mode: HoldMode;
  investment_type?: string | null;
  allowed_hold_modes?: HoldMode[];
  held_currencies?: string[];
  held_markets?: string[];
}

export const ProductModal: React.FC<ProductModalProps> = ({ open, onOpenChange, initialData, onSave, mode }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  // Agent-changed field paths converged onto a single source — the same
  // `agentChangedPaths` array that drives the main panel's orange
  // borders. The change-detection hook walks both the schema-driven
  // (group.field) and the product-specific (product.<section>.<isin>.<field>)
  // diffs and merges everything into this list. We filter to the
  // product subtree here so each modal cell can check membership with
  // a single `includes` lookup, identical to how `InputConstraints`
  // checks the main panel.
  const agentChangedPaths = constraintsStore((s) => s.agentChangedPaths);
  const productLists = activityPanelStore((state) => state.productLists);
  const fetchProductLists = activityPanelStore((state) => state.fetchProductLists);
  const productDisplayCache = activityPanelStore((state) => state.productDisplayCache);
  const pendingDisplayIsins = activityPanelStore((state) => state.pendingDisplayIsins);
  const portfolioProductValues = activityPanelStore((state) => state.portfolioProductValues);
  const fetchProductDisplayData = activityPanelStore((state) => state.fetchProductDisplayData);

  // Refetch product lists each time the modal opens so newly-created lists are visible.
  useEffect(() => {
    if (open) {
      fetchProductLists();
    }
  }, [open]);

  // Determine effective mode (diagnosis renders like builder)
  const effectiveMode = mode === 'diagnosis' ? 'builder' : mode;
  const isRebalancing = effectiveMode === 'rebalancing';
  const showPesoColumns = !isRebalancing || SHOW_PESO_COLUMNS_IN_REBALANCING;

  // Track which fields are currently being edited (focused) - for delayed validation note
  const [, setFocusedFields] = useState<Set<string>>(new Set());

  // Canonical product field path. Matches the shape produced by
  // `diffProductPaths` in `utils/constraintDocument.ts`, so a single
  // `agentChangedPaths.includes(path)` lookup decides the orange border.
  const productFieldPath = (
    section: 'individual_products' | 'list_products' | 'portfolio_products',
    isin: string,
    field: 'weight_min' | 'weight_max' | 'value_min' | 'value_max' | 'hold_mode',
  ) => `product.${section}.${isin}.${field}`;

  const handleFieldFocus = (
    section: 'individual_products' | 'list_products' | 'portfolio_products',
    isin: string,
    field: 'weight_min' | 'weight_max' | 'value_min' | 'value_max',
  ) => {
    const key = productFieldPath(section, isin, field);
    setFocusedFields((prev) => new Set(prev).add(key));
  };

  const handleFieldBlur = (
    section: 'individual_products' | 'list_products' | 'portfolio_products',
    isin: string,
    field: 'weight_min' | 'weight_max' | 'value_min' | 'value_max',
  ) => {
    const key = productFieldPath(section, isin, field);
    setFocusedFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  };

  // User typed over an agent-changed field — drop that field from the
  // global agent-change memory so the orange border clears. Idempotent
  // when the field wasn't in the list (the user's first edit on a
  // never-changed-by-agent field is a no-op).
  const markFieldEdited = (
    section: 'individual_products' | 'list_products' | 'portfolio_products',
    isin: string,
    field: 'weight_min' | 'weight_max' | 'value_min' | 'value_max' | 'hold_mode',
  ) => {
    const path = productFieldPath(section, isin, field);
    if (agentChangedPaths.includes(path)) {
      constraintsStore.getState().acknowledgeFieldEdit(path);
    }
  };

  const isFieldChanged = (
    section: 'individual_products' | 'list_products' | 'portfolio_products',
    isin: string,
    field: 'weight_min' | 'weight_max' | 'value_min' | 'value_max' | 'hold_mode',
  ) => agentChangedPaths.includes(productFieldPath(section, isin, field));

  // Local state for editing (uses 0-100 scale for UI display)
  const [individualProducts, setIndividualProducts] = useState<Record<string, ProductLocal>>({});
  const [listProducts, setListProducts] = useState<Record<string, ProductLocal>>({});
  const [portfolioProducts, setPortfolioProducts] = useState<Record<string, PortfolioProductLocal>>({});
  const [selectedListIds, setSelectedListIds] = useState<Set<string>>(new Set());
  const [onlyListProducts, setOnlyListProducts] = useState<boolean>(true);

  // Refs to store initial state when modal opens
  const initialIndividualProductsRef = useRef<Record<string, ProductLocal>>({});
  const initialListProductsRef = useRef<Record<string, ProductLocal>>({});
  const initialPortfolioProductsRef = useRef<Record<string, PortfolioProductLocal>>({});
  const initialSelectedListIdsRef = useRef<Set<string>>(new Set());

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const { fetchSearchProducts, productsSearchResults, resetProductsSearchResults, isSearchingProducts } =
    constraintsProductMicrocatsStore();

  // Product lists dropdown state
  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  const [pendingListIds, setPendingListIds] = useState<Set<string>>(new Set());
  const listDropdownRef = useRef<HTMLDivElement>(null);

  // Initialize local state from props when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSearchResults([]);
      resetProductsSearchResults();
      // No per-modal "user edited this" state to reset — the
      // store-level `agentChangedPaths` is the single source of truth
      // for orange borders, and `acknowledgeFieldEdit` mutates it
      // directly on every user keystroke. Re-opening the modal
      // therefore reflects the current global state.

      const toDisplayString = (value: number | null | undefined): string => {
        if (value == null) return '';
        return value.toFixed(2);
      };

      // Convert individual products
      const productsLocal: Record<string, ProductLocal> = {};
      const individualIsins: string[] = [];
      for (const [isin, product] of Object.entries(initialData?.individual_products ?? {})) {
        individualIsins.push(isin);
        productsLocal[isin] = {
          weight_min: toDisplayString(apiToUiScale(product.weight_min)),
          weight_max: toDisplayString(apiToUiScale(product.weight_max)),
          value_min: toDisplayString(product.value_min),
          value_max: toDisplayString(product.value_max),
        };
      }
      setIndividualProducts(productsLocal);
      initialIndividualProductsRef.current = structuredClone(productsLocal);

      // Convert list products
      const listProductsLocal: Record<string, ProductLocal> = {};
      const listIsins: string[] = [];
      for (const [isin, product] of Object.entries(initialData?.list_products ?? {})) {
        listIsins.push(isin);
        listProductsLocal[isin] = {
          weight_min: toDisplayString(apiToUiScale(product.weight_min)),
          weight_max: toDisplayString(apiToUiScale(product.weight_max)),
          value_min: toDisplayString(product.value_min),
          value_max: toDisplayString(product.value_max),
        };
      }
      setListProducts(listProductsLocal);
      initialListProductsRef.current = structuredClone(listProductsLocal);

      // Convert portfolio products (rebalancing mode only)
      let portfolioLocal: Record<string, PortfolioProductLocal> = {};
      const portfolioIsins: string[] = [];
      if (isRebalancing && initialData?.portfolio_products) {
        for (const [isin, product] of Object.entries(initialData.portfolio_products)) {
          portfolioIsins.push(isin);
          portfolioLocal[isin] = {
            weight_min: toDisplayString(apiToUiScale(product.weight_min)),
            weight_max: toDisplayString(apiToUiScale(product.weight_max)),
            value_min: toDisplayString(product.value_min),
            value_max: toDisplayString(product.value_max),
            hold_mode: product.hold_mode ?? 'none',
            investment_type: product.investment_type,
            allowed_hold_modes: product.allowed_hold_modes,
            held_currencies: product.held_currencies,
            held_markets: product.held_markets,
          };
        }
      }
      setPortfolioProducts(portfolioLocal);
      initialPortfolioProductsRef.current = structuredClone(portfolioLocal);

      // Set selected list IDs
      const listIds = new Set(initialData?.selected_list_ids ?? []);
      setSelectedListIds(listIds);
      setPendingListIds(listIds);
      initialSelectedListIdsRef.current = new Set(listIds);

      // Set only_list_products flag
      setOnlyListProducts(initialData?.only_list_products ?? true);

      // Fetch display data for all ISINs, passing investment_type for portfolio products
      const allItems: ProductDisplayRequestItem[] = [
        ...individualIsins.map((isin) => ({ isin, investment_type: null as string | null })),
        ...listIsins.map((isin) => ({ isin, investment_type: null as string | null })),
        ...portfolioIsins.map((isin) => ({
          isin,
          investment_type: initialData?.portfolio_products?.[isin]?.investment_type ?? null,
        })),
      ];
      if (allItems.length > 0) {
        fetchProductDisplayData(allItems);
      }
      // Portfolio values are synced from metadata via useConstraintsChangeDetection
    }
  }, [open, initialData, isRebalancing, fetchProductDisplayData]);

  // Search debounce
  useEffect(() => {
    if (searchTerm.length === 0) {
      resetProductsSearchResults();
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      fetchSearchProducts(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update search results when store updates
  useEffect(() => {
    if (searchTerm.length > 0) {
      setSearchResults(productsSearchResults);
    } else {
      setSearchResults([]);
    }
  }, [productsSearchResults, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (listDropdownRef.current && !listDropdownRef.current.contains(event.target as Node)) {
        setListDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add product from search
  const handleAddProduct = (product: { isin: string; descrizione_titolo: string; divisa?: string }) => {
    if (!individualProducts[product.isin]) {
      setIndividualProducts((prev) => ({
        ...prev,
        [product.isin]: {
          weight_min: '',
          weight_max: '',
          value_min: '',
          value_max: '',
        },
      }));
      // Fetch display data for the new product (no investment_type context for freshly-searched products)
      fetchProductDisplayData([{ isin: product.isin, investment_type: null }]);
    }
    setSearchTerm('');
    resetProductsSearchResults();
    setSearchResults([]);
  };

  // Remove individual product
  const handleRemoveProduct = (isin: string) => {
    setIndividualProducts((prev) => {
      const newProducts = { ...prev };
      delete newProducts[isin];
      return newProducts;
    });
  };

  // `source` is `'prop'` when react-number-format re-emits the value on
  // mount / prop-driven normalisation (e.g. "2.00" -> "2"). Skip the
  // markFieldEdited call in that case — opening the modal must not
  // appear as a user edit and must not clear the agent's orange
  // border. Only genuine user input (`'event'`) is treated as an edit.

  // Update individual product weight (clears value constraints for mutual exclusivity)
  const handleProductWeightChange = (
    isin: string,
    field: 'weight_min' | 'weight_max',
    value: string,
    source?: 'event' | 'prop',
  ) => {
    if (source !== 'prop') {
      markFieldEdited('individual_products', isin, field);
    }
    setIndividualProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        [field]: value,
        // Clear value constraints when setting weight (mutual exclusivity)
        value_min: '',
        value_max: '',
      },
    }));
  };

  // Update individual product controvalore (clears weight constraints for mutual exclusivity)
  const handleProductValueChange = (
    isin: string,
    field: 'value_min' | 'value_max',
    value: string,
    source?: 'event' | 'prop',
  ) => {
    if (source !== 'prop') {
      markFieldEdited('individual_products', isin, field);
    }
    setIndividualProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        [field]: value,
        // Clear weight constraints when setting value (mutual exclusivity)
        weight_min: '',
        weight_max: '',
      },
    }));
  };

  // Update list product weight (clears value constraints for mutual exclusivity)
  const handleListProductWeightChange = (
    isin: string,
    field: 'weight_min' | 'weight_max',
    value: string,
    source?: 'event' | 'prop',
  ) => {
    if (source !== 'prop') {
      markFieldEdited('list_products', isin, field);
    }
    setListProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        [field]: value,
        // Clear value constraints when setting weight (mutual exclusivity)
        value_min: '',
        value_max: '',
      },
    }));
  };

  // Update list product controvalore (clears weight constraints for mutual exclusivity)
  const handleListProductValueChange = (
    isin: string,
    field: 'value_min' | 'value_max',
    value: string,
    source?: 'event' | 'prop',
  ) => {
    if (source !== 'prop') {
      markFieldEdited('list_products', isin, field);
    }
    setListProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        [field]: value,
        // Clear weight constraints when setting value (mutual exclusivity)
        weight_min: '',
        weight_max: '',
      },
    }));
  };

  // Update portfolio product weight (clears value constraints for mutual exclusivity)
  const handlePortfolioWeightChange = (
    isin: string,
    field: 'weight_min' | 'weight_max',
    value: string,
    source?: 'event' | 'prop',
  ) => {
    if (source !== 'prop') {
      markFieldEdited('portfolio_products', isin, field);
    }
    setPortfolioProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        [field]: value,
        // Clear value constraints when setting weight (mutual exclusivity)
        value_min: '',
        value_max: '',
      },
    }));
  };

  // Update portfolio product controvalore (clears weight constraints for mutual exclusivity)
  const handlePortfolioValueChange = (
    isin: string,
    field: 'value_min' | 'value_max',
    value: string,
    source?: 'event' | 'prop',
  ) => {
    if (source !== 'prop') {
      markFieldEdited('portfolio_products', isin, field);
    }
    setPortfolioProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        [field]: value,
        // Clear weight constraints when setting value (mutual exclusivity)
        weight_min: '',
        weight_max: '',
      },
    }));
  };

  // Set portfolio product hold mode
  const handleSetHoldMode = (isin: string, holdMode: HoldMode) => {
    markFieldEdited('portfolio_products', isin, 'hold_mode');
    setPortfolioProducts((prev) => ({
      ...prev,
      [isin]: {
        ...prev[isin],
        hold_mode: holdMode,
        // Clear all constraints when full lock is selected
        ...(holdMode === 'hold' && {
          weight_min: '',
          weight_max: '',
          value_min: '',
          value_max: '',
        }),
      },
    }));
  };

  // Check if all non-locked portfolio product weights are already zero (explicitly set, not empty)
  const allPositionsAlreadySold = useMemo(() => {
    const entries = Object.entries(portfolioProducts);
    const sellableEntries = entries.filter(([, product]) => product.hold_mode !== 'hold');
    if (sellableEntries.length === 0) return true;
    return sellableEntries.every(([, product]) => {
      if (showPesoColumns) {
        const minStr = product.weight_min.trim();
        const maxStr = product.weight_max.trim();
        if (minStr === '' || maxStr === '') return false;
        return parseFloat(minStr) === 0 && parseFloat(maxStr) === 0;
      } else {
        const minStr = product.value_min.trim();
        const maxStr = product.value_max.trim();
        if (minStr === '' || maxStr === '') return false;
        return parseFloat(minStr) === 0 && parseFloat(maxStr) === 0;
      }
    });
  }, [portfolioProducts, showPesoColumns]);

  const handleSellAllPositions = () => {
    setPortfolioProducts((prev) => {
      const updated: Record<string, PortfolioProductLocal> = {};
      for (const [isin, product] of Object.entries(prev)) {
        if (product.hold_mode === 'hold') {
          updated[isin] = product;
        } else if (showPesoColumns) {
          // Current behavior: set peso to 0, clear controvalore
          updated[isin] = {
            ...product,
            value_min: '',
            value_max: '',
            weight_min: '0',
            weight_max: '0',
          };
        } else {
          // Peso hidden: set controvalore to 0, clear peso
          updated[isin] = {
            ...product,
            value_min: '0',
            value_max: '0',
            weight_min: '',
            weight_max: '',
          };
        }
      }
      return updated;
    });
  };

  // Toggle list selection in dropdown
  const handleToggleListSelection = (listId: string) => {
    setPendingListIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };

  // Confirm list selection - apply pending changes
  const handleConfirmListSelection = async () => {
    const addedListIds = Array.from(pendingListIds).filter((id) => !selectedListIds.has(id));
    const removedListIds = Array.from(selectedListIds).filter((id) => !pendingListIds.has(id));

    // Process removed lists - check if products belong to any other selected list
    if (removedListIds.length > 0) {
      setListProducts((prev) => {
        const newListProducts = { ...prev };
        const remainingSelectedIds = Array.from(pendingListIds);

        for (const isin of Object.keys(newListProducts)) {
          // Check if this ISIN exists in any of the remaining selected lists
          const listsWithProduct = getListsContainingProduct(isin, productLists, remainingSelectedIds);
          if (listsWithProduct.length === 0) {
            // No remaining lists contain this product - remove it
            delete newListProducts[isin];
          }
        }
        return newListProducts;
      });
    }

    // Process added lists - fetch products and add them
    const newIsins: string[] = [];
    for (const listId of addedListIds) {
      const list = productLists.find((l) => l.id === listId);
      if (list && list.products) {
        setListProducts((prev) => {
          const newListProducts = { ...prev };
          for (const product of list.products) {
            const isin = product.isin;
            if (!newListProducts[isin]) {
              // New product - add it with empty constraints
              newListProducts[isin] = {
                weight_min: '',
                weight_max: '',
                value_min: '',
                value_max: '',
              };
              newIsins.push(isin);
            }
          }
          return newListProducts;
        });
      }
    }

    // Fetch display data for new products (list products have no investment_type context)
    if (newIsins.length > 0) {
      fetchProductDisplayData(newIsins.map((isin) => ({ isin, investment_type: null })));
    }

    setSelectedListIds(new Set(pendingListIds));
    setListDropdownOpen(false);
  };

  // Remove a single list directly (from pill × button)
  const handleRemoveList = (listId: string) => {
    const remainingIds = new Set(selectedListIds);
    remainingIds.delete(listId);

    // Remove products that no longer belong to any selected list
    setListProducts((prev) => {
      const newListProducts = { ...prev };
      const remainingArray = Array.from(remainingIds);

      for (const isin of Object.keys(newListProducts)) {
        const listsWithProduct = getListsContainingProduct(isin, productLists, remainingArray);
        if (listsWithProduct.length === 0) {
          delete newListProducts[isin];
        }
      }
      return newListProducts;
    });

    setSelectedListIds(remainingIds);
    setPendingListIds(remainingIds);
  };

  // Reset to initial state
  const handleReset = () => {
    setIndividualProducts(structuredClone(initialIndividualProductsRef.current));
    setListProducts(structuredClone(initialListProductsRef.current));
    setPortfolioProducts(structuredClone(initialPortfolioProductsRef.current));
    setSelectedListIds(new Set(initialSelectedListIdsRef.current));
    setPendingListIds(new Set(initialSelectedListIdsRef.current));
    setOnlyListProducts(initialData?.only_list_products ?? true);
    setSearchTerm('');
    resetProductsSearchResults();
    setSearchResults([]);
  };

  // Save changes
  const handleSave = () => {
    const parseToNumber = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const num = parseFloat(value);
      return isNaN(num) ? null : num;
    };

    // Convert from 0-100 scale back to 0-1 scale
    // In builder mode, value constraints are not available, so always set to null
    const productsApi: Record<string, ProductItemConstraint> = {};
    for (const [isin, product] of Object.entries(individualProducts)) {
      productsApi[isin] = {
        weight_min: uiToApiScale(parseToNumber(product.weight_min)),
        weight_max: uiToApiScale(parseToNumber(product.weight_max)),
        value_min: isRebalancing ? parseToNumber(product.value_min) : null,
        value_max: isRebalancing ? parseToNumber(product.value_max) : null,
      };
    }

    const listProductsApi: Record<string, ProductItemConstraint> = {};
    for (const [isin, product] of Object.entries(listProducts)) {
      listProductsApi[isin] = {
        weight_min: uiToApiScale(parseToNumber(product.weight_min)),
        weight_max: uiToApiScale(parseToNumber(product.weight_max)),
        value_min: isRebalancing ? parseToNumber(product.value_min) : null,
        value_max: isRebalancing ? parseToNumber(product.value_max) : null,
      };
    }

    // Convert portfolio products (rebalancing mode only)
    let portfolioProductsApi: Record<string, PortfolioProductConstraint> | null = null;
    if (isRebalancing && Object.keys(portfolioProducts).length > 0) {
      portfolioProductsApi = {};
      for (const [isin, product] of Object.entries(portfolioProducts)) {
        portfolioProductsApi[isin] = {
          weight_min: uiToApiScale(parseToNumber(product.weight_min)),
          weight_max: uiToApiScale(parseToNumber(product.weight_max)),
          value_min: parseToNumber(product.value_min),
          value_max: parseToNumber(product.value_max),
          hold_mode: product.hold_mode,
          investment_type: product.investment_type,
          allowed_hold_modes: product.allowed_hold_modes,
          held_currencies: product.held_currencies,
          held_markets: product.held_markets,
        };
      }
    }

    const finalIndividualProducts = productsApi;
    const finalListProducts = listProductsApi;
    const finalSelectedListIds = Array.from(selectedListIds);

    const data: ProductConstraints = {
      individual_products: finalIndividualProducts,
      list_products: finalListProducts,
      selected_list_ids: finalSelectedListIds,
      portfolio_products: portfolioProductsApi,
      only_list_products: onlyListProducts,
    };

    onSave(data);
    onOpenChange(false);
  };

  // Helper to get display data for a product
  const getDisplayLabel = (isin: string): string => {
    return productDisplayCache[isin]?.label ?? isin;
  };

  const getDisplayCurrency = (isin: string): string => {
    return productDisplayCache[isin]?.currency ?? '-';
  };

  const getConsultabilityDisplay = (isin: string): { consultable: string; maintainable: string } => {
    const status = productDisplayCache[isin]?.consultability_status;
    return {
      consultable: status === 'consultable' ? 'Si' : 'No',
      maintainable: status === 'maintainable' ? 'Si' : 'No',
    };
  };

  // Portfolio rows show the currencies of the held listings; old threads
  // without them fall back to the builder row's currency.
  const getPortfolioCurrency = (isin: string): string => {
    const held = portfolioProducts[isin]?.held_currencies;
    if (!held) return getDisplayCurrency(isin);
    return held.length ? held.join(', ') : '-';
  };

  // Portfolio rows carry the hold modes of the held listing (set at import);
  // the per-ISIN display cache is the fallback for rows saved without them.
  const getAllowedHoldModes = (isin: string): HoldMode[] | undefined =>
    portfolioProducts[isin]?.allowed_hold_modes?.length
      ? portfolioProducts[isin].allowed_hold_modes
      : productDisplayCache[isin]?.allowed_hold_modes;

  // Helper to check if a product is disabled (non-consultable or maintainable)
  const isProductDisabled = (isin: string): boolean => {
    const status = productDisplayCache[isin]?.consultability_status;
    return !!status && status !== 'consultable';
  };

  const hasDisabledIndividualProducts = useMemo(() => {
    return Object.keys(individualProducts).some((isin) => isProductDisabled(isin));
  }, [individualProducts, productDisplayCache]);

  const hasDisabledListProducts = useMemo(() => {
    return Object.keys(listProducts).some((isin) => isProductDisabled(isin));
  }, [listProducts, productDisplayCache]);

  const isProductLoading = (isin: string): boolean => {
    return !!pendingDisplayIsins[isin] && !productDisplayCache[isin];
  };

  // For the list-products table we want a uniform look: if any row is still
  // loading, all rows render placeholders. Reads cleaner when an entire list
  // hydrates at once vs. some rows showing names while others show skeletons.
  const isAnyListProductLoading = useMemo(() => {
    return Object.keys(listProducts).some((isin) => isProductLoading(isin));
  }, [listProducts, pendingDisplayIsins, productDisplayCache]);

  // Helper to determine which constraint type is active for a product
  // Returns: 'none' (all empty), 'value' (value constraints set), 'weight' (weight constraints set)
  const getActiveConstraintType = (product: ProductLocal): 'none' | 'value' | 'weight' => {
    const hasValue = product.value_min?.trim() !== '' || product.value_max?.trim() !== '';
    const hasWeight = product.weight_min?.trim() !== '' || product.weight_max?.trim() !== '';
    if (!showPesoColumns) {
      // When peso columns are hidden, ignore weight fields to prevent stale data from locking controvalore inputs
      return hasValue ? 'value' : 'none';
    }
    if (hasValue) return 'value';
    if (hasWeight) return 'weight';
    return 'none';
  };

  // Validation: check if min > max for a constraint pair
  // Returns error type: 'weight' | 'value' | null
  const validateMinMax = (product: ProductLocal): { weightError: boolean; valueError: boolean } => {
    const parseValue = (val: string): number | null => {
      if (!val || val.trim() === '') return null;
      const num = parseFloat(val);
      return isNaN(num) ? null : num;
    };

    const weightMin = parseValue(product.weight_min);
    const weightMax = parseValue(product.weight_max);
    const valueMin = parseValue(product.value_min);
    const valueMax = parseValue(product.value_max);

    // Skip weight validation when peso columns are hidden — users can't see or fix the error
    const weightError = showPesoColumns && weightMin !== null && weightMax !== null && weightMin > weightMax;
    // Check value constraints: error if both are set and min > max
    const valueError = valueMin !== null && valueMax !== null && valueMin > valueMax;

    return { weightError, valueError };
  };

  // Compute all validation errors across all products
  const validationErrors = useMemo(() => {
    const errors: {
      individual: Record<string, { weightError: boolean; valueError: boolean }>;
      list: Record<string, { weightError: boolean; valueError: boolean }>;
      portfolio: Record<string, { weightError: boolean; valueError: boolean }>;
      hasErrors: boolean;
    } = {
      individual: {},
      list: {},
      portfolio: {},
      hasErrors: false,
    };

    // Validate individual products
    for (const [isin, product] of Object.entries(individualProducts)) {
      const validation = validateMinMax(product);
      if (validation.weightError || validation.valueError) {
        errors.individual[isin] = validation;
        errors.hasErrors = true;
      }
    }

    // Validate list products
    for (const [isin, product] of Object.entries(listProducts)) {
      const validation = validateMinMax(product);
      if (validation.weightError || validation.valueError) {
        errors.list[isin] = validation;
        errors.hasErrors = true;
      }
    }

    // Validate portfolio products
    for (const [isin, product] of Object.entries(portfolioProducts)) {
      const validation = validateMinMax(product);
      if (validation.weightError || validation.valueError) {
        errors.portfolio[isin] = validation;
        errors.hasErrors = true;
      }
    }

    return errors;
  }, [individualProducts, listProducts, portfolioProducts, showPesoColumns]);

  // Helper to get portfolio values (from metadata synced to store)
  const getPortfolioCurrentValue = (isin: string): number | undefined => {
    return portfolioProductValues[isin]?.current_value;
  };

  const getPortfolioTargetValue = (isin: string): number | null | undefined => {
    return portfolioProductValues[isin]?.target_value;
  };

  const getPortfolioCurrentWeight = (isin: string): number | undefined => {
    const weight = portfolioProductValues[isin]?.current_weight;
    return weight != null ? (apiToUiScale(weight) ?? undefined) : undefined;
  };

  const getPortfolioTargetWeight = (isin: string): number | null | undefined => {
    const weight = portfolioProductValues[isin]?.target_weight;
    return weight != null ? apiToUiScale(weight) : null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showHeader={false}
        className={styles.modalContent}
        bodyClassName={styles.dialogBodyOverflow}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        xColor={colorsConfig.FinecoBluePrimary}
      >
        <div className={styles.customHeader}>
          <div className={styles.headerLogo}>
            <img src={finecoLogo} alt="Fineco" width={32} height={32} />
          </div>
          <h2 className={styles.headerTitle}>{t('constraints.product.modal.title')}</h2>
          <div className={styles.headerSpacer} />
        </div>

        {/* Fixed Search Section */}
        <div className={styles.searchSection}>
            <div className={styles.searchWrapper}>
              <span className={styles.searchLabel}>{t('constraints.product.modal.searchLabel')}</span>
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  placeholder={t('constraints.product.modal.searchProducts')}
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm.length === 0 ? (
                  <Search className={styles.searchIcon} size={16} color={colorsConfig.FinecoBluePrimary} />
                ) : (
                  <X
                    className={styles.clearSearchIcon}
                    size={16}
                    color={colorsConfig.FinecoBluePrimary}
                    onClick={() => setSearchTerm('')}
                  />
                )}
              </div>
              {(searchResults.length > 0 || isSearchingProducts) && (
                <div className={styles.dropdownContainer}>
                  {isSearchingProducts ? (
                    <div className={styles.dropdownItem}>{t('constraints.product.modal.searching')}</div>
                  ) : (
                    searchResults.map((product) => (
                      <div key={product.isin} className={styles.dropdownItem} onClick={() => handleAddProduct(product)}>
                        {product.isin} - {product.descrizione_titolo}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

        {/* Scrollable Content Area */}
        <div className={styles.scrollableContent}>
          {/* Portfolio Products Section (Rebalancing mode only) */}
          {isRebalancing && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('constraints.product.modal.portfolioProducts')}</h3>
              {Object.keys(portfolioProducts).length > 0 ? (
                <>
                  <div className={styles.tableContainer}>
                    <table className={styles.constraintsTable}>
                      <thead>
                        <tr>
                          <th>{t('constraints.product.modal.table.isin')}</th>
                          <th>{t('constraints.product.modal.table.name')}</th>
                          <th>{t('constraints.product.modal.table.typology')}</th>
                          <th>{t('instrumentLists.table.valuta')}</th>
                          <th className={styles.dualColumnHeader}>
                            <span>{t('constraints.product.modal.table.valueInitialFinal')}</span>
                          </th>
                          <th>{t('constraints.product.modal.table.valueMin')}</th>
                          <th>{t('constraints.product.modal.table.valueMax')}</th>
                          <th className={styles.dualColumnHeader}>
                            <span>{t('constraints.product.modal.table.weightInitialFinal')}</span>
                          </th>
                          {showPesoColumns && <th>{t('constraints.product.modal.table.weightMin')}</th>}
                          {showPesoColumns && <th>{t('constraints.product.modal.table.weightMax')}</th>}
                          {/* TODO: P&L column hidden for Fineco presentation (2026-03-30). Uncomment to re-enable. */}
                          {/* <th>{t('constraints.product.modal.table.pnl')}</th> */}
                          <th>{t('constraints.product.modal.table.holdMode')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(portfolioProducts).map(([isin, product]) => (
                          <tr key={isin}>
                            <td
                              className={styles.isinCell}
                              title={product.held_markets?.length ? `Mercato: ${product.held_markets.join(', ')}` : undefined}
                            >
                              {isin}
                            </td>
                            <td className={styles.productName} title={getDisplayLabel(isin)}>
                              {getDisplayLabel(isin)}
                            </td>
                            <td>{product.investment_type === 'PAC' ? 'PAC' : 'PIC'}</td>
                            <td>{getPortfolioCurrency(isin)}</td>
                            <td className={styles.dualValueCell}>
                              <span>{formatItalianNumber(getPortfolioCurrentValue(isin), 2)}</span>
                              <span>{formatItalianNumber(getPortfolioTargetValue(isin), 2)}</span>
                            </td>
                            {(() => {
                              const constraintType = getActiveConstraintType(product);
                              const isValueDisabled = product.hold_mode === 'hold' || constraintType === 'weight';
                              const isWeightDisabled = product.hold_mode === 'hold' || constraintType === 'value';
                              return (
                                <>
                                  <td>
                                    <AmountInput
                                      className={`${styles.valueInput} ${isFieldChanged('portfolio_products', isin, 'value_min') ? styles.changedInput : ''}`}
                                      value={product.value_min}
                                      onChangeValue={(v, source) => handlePortfolioValueChange(isin, 'value_min', v, source)}
                                      placeholder={t('constraints.product.modal.inputPlaceholder')}
                                      disabled={isValueDisabled}
                                      onFocus={() => handleFieldFocus('portfolio_products', isin, 'value_min')}
                                      onBlur={() => handleFieldBlur('portfolio_products', isin, 'value_min')}
                                    />
                                  </td>
                                  <td>
                                    <AmountInput
                                      className={`${styles.valueInput} ${isFieldChanged('portfolio_products', isin, 'value_max') ? styles.changedInput : ''}`}
                                      value={product.value_max}
                                      onChangeValue={(v, source) => handlePortfolioValueChange(isin, 'value_max', v, source)}
                                      placeholder={t('constraints.product.modal.inputPlaceholder')}
                                      disabled={isValueDisabled}
                                      onFocus={() => handleFieldFocus('portfolio_products', isin, 'value_max')}
                                      onBlur={() => handleFieldBlur('portfolio_products', isin, 'value_max')}
                                    />
                                  </td>
                                  <td className={styles.dualValueCell}>
                                    <span>{formatItalianNumber(getPortfolioCurrentWeight(isin), 2)}</span>
                                    <span>{formatItalianNumber(getPortfolioTargetWeight(isin), 2)}</span>
                                  </td>
                                  {showPesoColumns && (
                                    <td>
                                      <AmountInput
                                        className={`${styles.weightInput} ${isFieldChanged('portfolio_products', isin, 'weight_min') ? styles.changedInput : ''}`}
                                        value={product.weight_min}
                                        onChangeValue={(v, source) => handlePortfolioWeightChange(isin, 'weight_min', v, source)}
                                        placeholder={t('constraints.product.modal.inputPlaceholder')}
                                        disabled={isWeightDisabled}
                                        onFocus={() => handleFieldFocus('portfolio_products', isin, 'weight_min')}
                                        onBlur={() => handleFieldBlur('portfolio_products', isin, 'weight_min')}
                                      />
                                    </td>
                                  )}
                                  {showPesoColumns && (
                                    <td>
                                      <AmountInput
                                        className={`${styles.weightInput} ${isFieldChanged('portfolio_products', isin, 'weight_max') ? styles.changedInput : ''}`}
                                        value={product.weight_max}
                                        onChangeValue={(v, source) => handlePortfolioWeightChange(isin, 'weight_max', v, source)}
                                        placeholder={t('constraints.product.modal.inputPlaceholder')}
                                        disabled={isWeightDisabled}
                                        onFocus={() => handleFieldFocus('portfolio_products', isin, 'weight_max')}
                                        onBlur={() => handleFieldBlur('portfolio_products', isin, 'weight_max')}
                                      />
                                    </td>
                                  )}
                                </>
                              );
                            })()}
                            {/* TODO: P&L cell hidden for Fineco presentation (2026-03-30). Uncomment to re-enable. */}
                            {/* <td className={styles.pnlCell}>
                              {formatPnl(getPortfolioCurrentValue(isin), getPortfolioTargetValue(isin))}
                            </td> */}
                            <td>
                              <div className={styles.holdModeToggle}>
                                <button
                                  type="button"
                                  className={`${styles.holdModeButton} ${product.hold_mode === 'none' ? styles.active : ''}`}
                                  onClick={() => handleSetHoldMode(isin, 'none')}
                                  disabled={!getAllowedHoldModes(isin)?.includes('none')}
                                  title={
                                    getAllowedHoldModes(isin)?.includes('none')
                                      ? t('constraints.product.modal.holdModeNone')
                                      : t('constraints.product.modal.holdModeLockedByType')
                                  }
                                >
                                  <Unlock width={16} height={16} />
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.holdModeButton} ${product.hold_mode === 'hold_or_sell' ? styles.active : ''}`}
                                  onClick={() => handleSetHoldMode(isin, 'hold_or_sell')}
                                  disabled={!getAllowedHoldModes(isin)?.includes('hold_or_sell')}
                                  title={
                                    getAllowedHoldModes(isin)?.includes('hold_or_sell')
                                      ? t('constraints.product.modal.holdModeHoldOrSell')
                                      : t('constraints.product.modal.holdModeLockedByType')
                                  }
                                >
                                  <LockSell width={16} height={16} />
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.holdModeButton} ${product.hold_mode === 'hold' ? styles.active : ''}`}
                                  onClick={() => handleSetHoldMode(isin, 'hold')}
                                  disabled={!getAllowedHoldModes(isin)?.includes('hold')}
                                  title={
                                    getAllowedHoldModes(isin)?.includes('hold')
                                      ? t('constraints.product.modal.holdModeHold')
                                      : t('constraints.product.modal.holdModeLockedByType')
                                  }
                                >
                                  <Lock width={16} height={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className={styles.sellAllPositionsButton}
                    onClick={handleSellAllPositions}
                    disabled={allPositionsAlreadySold}
                  >
                    <Wallet />
                    {t('constraints.product.modal.sellAllPositions')}
                  </button>
                </>
              ) : (
                <p className={styles.emptyState}>{t('constraints.product.modal.noPortfolioProducts')}</p>
              )}
            </div>
          )}

          {/* Added Instruments Table (from search) */}
          <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('constraints.product.modal.addedProducts')}</h3>
              {hasDisabledIndividualProducts && (
                <IconNote
                  type="attention"
                  title={t('constraints.product.modal.disabledProductsWarningTitle')}
                  body={t('constraints.product.modal.disabledProductsWarningBody')}
                  className={styles.disabledProductsNote}
                />
              )}
              {Object.keys(individualProducts).length > 0 ? (
                <div className={styles.tableContainer}>
                  <table
                    className={`${styles.constraintsTable} ${
                      isRebalancing
                        ? (showPesoColumns ? styles.constraintsTableFixedRebalancing : styles.constraintsTableFixedRebalancingNoPeso)
                        : styles.constraintsTableFixedBuilder
                    }`}
                  >
                    <thead>
                      <tr>
                        <th>{t('constraints.product.modal.table.isin')}</th>
                        <th>{t('constraints.product.modal.table.name')}</th>
                        <th>{t('instrumentLists.table.valuta')}</th>
                        <th>{t('constraints.product.modal.table.consultable')}</th>
                        <th>{t('constraints.product.modal.table.maintainable')}</th>
                        {isRebalancing && <th>{t('constraints.product.modal.table.valueMin')}</th>}
                        {isRebalancing && <th>{t('constraints.product.modal.table.valueMax')}</th>}
                        {showPesoColumns && <th>{t('constraints.product.modal.table.weightMin')}</th>}
                        {showPesoColumns && <th>{t('constraints.product.modal.table.weightMax')}</th>}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(individualProducts).map(([isin, product]) => {
                        const constraintType = getActiveConstraintType(product);
                        const isValueDisabled = constraintType === 'weight';
                        const isWeightDisabled = constraintType === 'value';
                        return (
                          <tr key={isin} className={isProductDisabled(isin) ? styles.disabledRow : undefined}>
                            <td className={styles.isinCell}>{isin}</td>
                            <td className={styles.productName} title={isProductLoading(isin) ? '' : getDisplayLabel(isin)}>
                              {isProductLoading(isin) ? <span className={styles.cellSkeleton} style={{ width: 160 }} /> : getDisplayLabel(isin)}
                            </td>
                            <td>{isProductLoading(isin) ? <span className={styles.cellSkeleton} style={{ width: 40 }} /> : getDisplayCurrency(isin)}</td>
                            <td>{isProductLoading(isin) ? <span className={styles.cellSkeleton} style={{ width: 24 }} /> : getConsultabilityDisplay(isin).consultable}</td>
                            <td>{isProductLoading(isin) ? <span className={styles.cellSkeleton} style={{ width: 24 }} /> : getConsultabilityDisplay(isin).maintainable}</td>
                            {isRebalancing && (
                              <td>
                                <AmountInput
                                  className={`${styles.valueInput} ${isFieldChanged('individual_products', isin, 'value_min') ? styles.changedInput : ''}`}
                                  value={product.value_min}
                                  onChangeValue={(v, source) => handleProductValueChange(isin, 'value_min', v, source)}
                                  placeholder={t('constraints.product.modal.inputPlaceholder')}
                                  disabled={isValueDisabled || isProductDisabled(isin)}
                                  onFocus={() => handleFieldFocus('individual_products', isin, 'value_min')}
                                  onBlur={() => handleFieldBlur('individual_products', isin, 'value_min')}
                                />
                              </td>
                            )}
                            {isRebalancing && (
                              <td>
                                <AmountInput
                                  className={`${styles.valueInput} ${isFieldChanged('individual_products', isin, 'value_max') ? styles.changedInput : ''}`}
                                  value={product.value_max}
                                  onChangeValue={(v, source) => handleProductValueChange(isin, 'value_max', v, source)}
                                  placeholder={t('constraints.product.modal.inputPlaceholder')}
                                  disabled={isValueDisabled || isProductDisabled(isin)}
                                  onFocus={() => handleFieldFocus('individual_products', isin, 'value_max')}
                                  onBlur={() => handleFieldBlur('individual_products', isin, 'value_max')}
                                />
                              </td>
                            )}
                            {showPesoColumns && (
                              <td>
                                <AmountInput
                                  className={`${styles.weightInput} ${isFieldChanged('individual_products', isin, 'weight_min') ? styles.changedInput : ''}`}
                                  value={product.weight_min}
                                  onChangeValue={(v, source) => handleProductWeightChange(isin, 'weight_min', v, source)}
                                  placeholder={t('constraints.product.modal.inputPlaceholder')}
                                  disabled={isWeightDisabled || isProductDisabled(isin)}
                                  onFocus={() => handleFieldFocus('individual_products', isin, 'weight_min')}
                                  onBlur={() => handleFieldBlur('individual_products', isin, 'weight_min')}
                                />
                              </td>
                            )}
                            {showPesoColumns && (
                              <td>
                                <AmountInput
                                  className={`${styles.weightInput} ${isFieldChanged('individual_products', isin, 'weight_max') ? styles.changedInput : ''}`}
                                  value={product.weight_max}
                                  onChangeValue={(v, source) => handleProductWeightChange(isin, 'weight_max', v, source)}
                                  placeholder={t('constraints.product.modal.inputPlaceholder')}
                                  disabled={isWeightDisabled || isProductDisabled(isin)}
                                  onFocus={() => handleFieldFocus('individual_products', isin, 'weight_max')}
                                  onBlur={() => handleFieldBlur('individual_products', isin, 'weight_max')}
                                />
                              </td>
                            )}
                            <td>
                              <button
                                type="button"
                                className={styles.removeButton}
                                onClick={() => handleRemoveProduct(isin)}
                              >
                                <CircleX size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={styles.emptyState}>{t('constraints.product.modal.noProductsAdded')}</p>
              )}
            </div>

          {/* Products from Lists Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeaderRow} ref={listDropdownRef}>
              <h3 className={styles.sectionTitle}>{t('constraints.product.modal.listProducts')}</h3>
              <button
                type="button"
                className={styles.addListLink}
                title={t('constraints.product.modal.addListTooltip')}
                onClick={() => {
                  setPendingListIds(new Set(selectedListIds));
                  setListDropdownOpen(!listDropdownOpen);
                }}
              >
                <CirclePlus size={16} />
                {t('constraints.product.modal.addList')}
              </button>
              {listDropdownOpen && (
                <div className={styles.listDropdown}>
                  <div className={styles.listDropdownItems}>
                    {productLists.map((list) => (
                      <label key={list.id} className={styles.listCheckboxItem}>
                        <input
                          type="checkbox"
                          checked={pendingListIds.has(list.id)}
                          onChange={() => handleToggleListSelection(list.id)}
                        />
                        <span>{list.name}</span>
                        <span className={styles.listProductCount}>({list.products.length})</span>
                      </label>
                    ))}
                    {productLists.length === 0 && (
                      <div className={styles.noLists}>{t('constraints.product.modal.noLists')}</div>
                    )}
                  </div>
                  <div className={styles.listDropdownFooter}>
                    <FinecoButton click={handleConfirmListSelection} copy={t('constraints.product.modal.confirm')} />
                  </div>
                </div>
              )}
            </div>
            {selectedListIds.size > 0 && (
              <div className={styles.listPills}>
                {productLists
                  .filter((list) => selectedListIds.has(list.id))
                  .map((list) => (
                    <span key={list.id} className={styles.listPill}>
                      {list.name}
                      <X
                        size={14}
                        className={styles.listPillRemove}
                        onClick={() => handleRemoveList(list.id)}
                      />
                    </span>
                  ))}
              </div>
            )}
            {hasDisabledListProducts && (
              <IconNote
                type="attention"
                title={t('constraints.product.modal.disabledProductsWarningTitle')}
                body={t('constraints.product.modal.disabledProductsWarningBody')}
                className={styles.disabledProductsNote}
              />
            )}
            {Object.keys(listProducts).length > 0 ? (
              <div className={styles.tableContainer}>
                <table
                  className={`${styles.constraintsTable} ${
                    isRebalancing
                      ? (showPesoColumns ? styles.constraintsTableFixedRebalancing : styles.constraintsTableFixedRebalancingNoPeso)
                      : styles.constraintsTableFixedBuilder
                  }`}
                >
                  <thead>
                    <tr>
                      <th>{t('constraints.product.modal.table.isin')}</th>
                      <th>{t('constraints.product.modal.table.name')}</th>
                      <th>{t('instrumentLists.table.valuta')}</th>
                      <th>{t('constraints.product.modal.table.consultable')}</th>
                      <th>{t('constraints.product.modal.table.maintainable')}</th>
                      {isRebalancing && <th>{t('constraints.product.modal.table.valueMin')}</th>}
                      {isRebalancing && <th>{t('constraints.product.modal.table.valueMax')}</th>}
                      {showPesoColumns && <th>{t('constraints.product.modal.table.weightMin')}</th>}
                      {showPesoColumns && <th>{t('constraints.product.modal.table.weightMax')}</th>}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(listProducts).map(([isin, product]) => {
                      const constraintType = getActiveConstraintType(product);
                      const isValueDisabled = constraintType === 'weight';
                      const isWeightDisabled = constraintType === 'value';
                      return (
                        <tr key={isin} className={isProductDisabled(isin) ? styles.disabledRow : undefined}>
                          <td className={styles.isinCell}>{isin}</td>
                          <td className={styles.productName} title={isAnyListProductLoading ? '' : getDisplayLabel(isin)}>
                            {isAnyListProductLoading ? <span className={styles.cellSkeleton} style={{ width: 160 }} /> : getDisplayLabel(isin)}
                          </td>
                          <td>{isAnyListProductLoading ? <span className={styles.cellSkeleton} style={{ width: 40 }} /> : getDisplayCurrency(isin)}</td>
                          <td>{isAnyListProductLoading ? <span className={styles.cellSkeleton} style={{ width: 24 }} /> : getConsultabilityDisplay(isin).consultable}</td>
                          <td>{isAnyListProductLoading ? <span className={styles.cellSkeleton} style={{ width: 24 }} /> : getConsultabilityDisplay(isin).maintainable}</td>
                          {isRebalancing && (
                            <td>
                              <AmountInput
                                className={`${styles.valueInput} ${isFieldChanged('list_products', isin, 'value_min') ? styles.changedInput : ''}`}
                                value={product.value_min}
                                onChangeValue={(v, source) => handleListProductValueChange(isin, 'value_min', v, source)}
                                placeholder={t('constraints.product.modal.inputPlaceholder')}
                                disabled={isValueDisabled || isProductDisabled(isin)}
                                onFocus={() => handleFieldFocus('list_products', isin, 'value_min')}
                                onBlur={() => handleFieldBlur('list_products', isin, 'value_min')}
                              />
                            </td>
                          )}
                          {isRebalancing && (
                            <td>
                              <AmountInput
                                className={`${styles.valueInput} ${isFieldChanged('list_products', isin, 'value_max') ? styles.changedInput : ''}`}
                                value={product.value_max}
                                onChangeValue={(v, source) => handleListProductValueChange(isin, 'value_max', v, source)}
                                placeholder={t('constraints.product.modal.inputPlaceholder')}
                                disabled={isValueDisabled || isProductDisabled(isin)}
                                onFocus={() => handleFieldFocus('list_products', isin, 'value_max')}
                                onBlur={() => handleFieldBlur('list_products', isin, 'value_max')}
                              />
                            </td>
                          )}
                          {showPesoColumns && (
                            <td>
                              <AmountInput
                                className={`${styles.weightInput} ${isFieldChanged('list_products', isin, 'weight_min') ? styles.changedInput : ''}`}
                                value={product.weight_min}
                                onChangeValue={(v, source) => handleListProductWeightChange(isin, 'weight_min', v, source)}
                                placeholder={t('constraints.product.modal.inputPlaceholder')}
                                disabled={isWeightDisabled || isProductDisabled(isin)}
                                onFocus={() => handleFieldFocus('list_products', isin, 'weight_min')}
                                onBlur={() => handleFieldBlur('list_products', isin, 'weight_min')}
                              />
                            </td>
                          )}
                          {showPesoColumns && (
                            <td>
                              <AmountInput
                                className={`${styles.weightInput} ${isFieldChanged('list_products', isin, 'weight_max') ? styles.changedInput : ''}`}
                                value={product.weight_max}
                                onChangeValue={(v, source) => handleListProductWeightChange(isin, 'weight_max', v, source)}
                                placeholder={t('constraints.product.modal.inputPlaceholder')}
                                disabled={isWeightDisabled || isProductDisabled(isin)}
                                onFocus={() => handleFieldFocus('list_products', isin, 'weight_max')}
                                onBlur={() => handleFieldBlur('list_products', isin, 'weight_max')}
                              />
                            </td>
                          )}
                          <td></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.emptyState}>{t('constraints.product.modal.noListProducts')}</p>
            )}

          </div>
        </div>

        {/* Validation error note - shown when there are errors */}
        {validationErrors.hasErrors && (
          <IconNote
            type="specific-error"
            title={t('constraints.product.modal.validationErrorTitle')}
            body={t('constraints.product.modal.validationErrorBody')}
            className={styles.validationErrorNote}
          />
        )}

        <DialogFooter className={styles.modalFooter}>
          {!isRebalancing && (selectedListIds.size > 0 || Object.keys(individualProducts).length > 0) && (
            <label className={styles.footerCheckboxLabel} style={{ color: '#000' }}>
              <input
                type="checkbox"
                checked={onlyListProducts}
                onChange={(e) => setOnlyListProducts(e.target.checked)}
              />
              <span style={{ color: '#000' }}>{t('constraints.product.modal.onlyListProducts')}</span>
            </label>
          )}
          <FinecoButton click={handleReset} variant="white" copy={t('constraints.product.modal.reset')} />
          <FinecoButton
            click={handleSave}
            copy={t('constraints.product.modal.save')}
            disabled={validationErrors.hasErrors}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
